import { useTranslation } from 'react-i18next';

import { SpecialNric } from '@shared/assets';
import type { NoFilterResultProps } from '@shared/components/report/NoFilterResult';
import { NoFilterResult } from '@shared/components/report/NoFilterResult';
import useOffline from '@shared/hooks/useOffline/useOffline';

import type { AccessNotGrantedProps } from '../AccessNotGranted/AccessNotGranted';
import AccessNotGranted from '../AccessNotGranted/AccessNotGranted';
import type { CommonEmptyStateScreenProps } from '../CommonEmptyStateScreen/CommonEmptyStateScreen';
import CommonEmptyStateScreen from '../CommonEmptyStateScreen/CommonEmptyStateScreen';
import { NehrOptOut } from '../NehrOptOut';
import type { NoInternetScreenProps } from '../NoInternetScreen/NoInternetScreen';
import NoInternetScreen from '../NoInternetScreen/NoInternetScreen';
import { RetrieveDataErrorDisplay } from '../RetrieveDataErrorDisplay';
import type { IRetrieveDataErrorDisplayProps } from '../RetrieveDataErrorDisplay/RetrieveDataErrorDisplay.types';

interface Props {
  accessNotGrantedProps?: {
    accessNotGrantedScreenProps: AccessNotGrantedProps;
    isNotGrantedAccess: boolean;
  };
  apiErrorProps?: {
    code?: null | string;
    errorScreenProps?: IRetrieveDataErrorDisplayProps;
    message?: null | string;
  };
  children: React.ReactNode;
  emptyStateProps?: {
    emptyStateScreenProps: CommonEmptyStateScreenProps;
    isEmpty: boolean;
  };
  nehrOptOutProps?: {
    isNehrOptOut: boolean;
  };
  noFilterResultProps?: {
    isEmpty: boolean;
    noFilterResultScreenProps: NoFilterResultProps;
  };
  noInternetScreenProps?: NoInternetScreenProps;
  specialNricErrorProps?: {
    isError: boolean;
    specialNricErrorScreenProps?: CommonEmptyStateScreenProps;
  };
  systemErrorProps?: {
    isError: boolean;
    systemErrorScreenProps?: NoInternetScreenProps;
  };
}

const ErrorScreenWrapper = ({
  accessNotGrantedProps,
  apiErrorProps,
  children,
  emptyStateProps,
  nehrOptOutProps,
  noFilterResultProps,
  noInternetScreenProps,
  specialNricErrorProps,
  systemErrorProps,
}: Props) => {
  const { t } = useTranslation();
  const isOffline = useOffline();

  if (isOffline) return <NoInternetScreen hideEmail hideTryAgainMsg {...noInternetScreenProps} />;

  if (systemErrorProps?.isError)
    return (
      <NoInternetScreen
        buttonName="Refresh"
        errorMsg="We are experiencing connectivity issues."
        {...systemErrorProps.systemErrorScreenProps}
      />
    );

  if (nehrOptOutProps?.isNehrOptOut) return <NehrOptOut />;

  if (specialNricErrorProps?.isError)
    return (
      <CommonEmptyStateScreen
        emptyImg={SpecialNric}
        message={t('dataErrorDisplay.specialNric')}
        {...specialNricErrorProps.specialNricErrorScreenProps}
      />
    );

  if (accessNotGrantedProps?.isNotGrantedAccess)
    return <AccessNotGranted {...accessNotGrantedProps.accessNotGrantedScreenProps} />;

  if (apiErrorProps?.code) return <RetrieveDataErrorDisplay {...apiErrorProps.errorScreenProps} />;

  if (noFilterResultProps?.isEmpty) return <NoFilterResult {...noFilterResultProps.noFilterResultScreenProps} />;

  if (emptyStateProps?.isEmpty) return <CommonEmptyStateScreen {...emptyStateProps.emptyStateScreenProps} />;

  return <>{children}</>;
};

export default ErrorScreenWrapper;
